/**
 * Iframe Security Utility
 *
 * Manages iframe access control by checking if the parent window's origin
 * matches allowed domains. Only specific Salesforce domains are permitted.
 *
 * Security Features:
 * - Origin validation and sanitization
 * - Protection against timing attacks
 * - Input validation
 * - Secure error handling
 */

import {
  ALLOWED_DOMAINS,
  getAllowedDomainPatterns,
  domainAllowsHttp,
  type AllowedDomainConfig,
} from './iframeSecurity.config';
import {
  isInIframe,
  isCurrentLocationLocalhost,
  isSecureContext,
  isLocalhostHostname,
  isDevelopmentEnv,
} from './iframeSecurity.utils';

/**
 * Maximum origin length to prevent DoS attacks
 */
const MAX_ORIGIN_LENGTH = 256;

/**
 * Validate and sanitize origin string
 * Returns sanitized origin or null if invalid
 *
 * @param origin - The origin string to validate
 * @param allowedDomains - Array of allowed domain configurations to check against
 */
function validateAndSanitizeOrigin(
  origin: string,
  allowedDomains: AllowedDomainConfig[] = ALLOWED_DOMAINS,
): string | null {
  if (!origin || typeof origin !== 'string') {
    return null;
  }

  // Check length to prevent DoS
  if (origin.length > MAX_ORIGIN_LENGTH) {
    if (isDevelopmentEnv()) {
      console.warn('Security: Origin exceeds maximum length');
    }
    return null;
  }

  // Basic format validation - must start with http:// or https://
  if (!origin.startsWith('https://') && !origin.startsWith('http://')) {
    return null;
  }

  // Remove any potential script injection attempts
  const sanitized = origin.replace(/[<>'"`]/g, '');

  // Validate URL structure
  try {
    const url = new URL(sanitized);

    // Check if any allowed domain configuration permits this origin
    const matchingDomain = allowedDomains.find((domainConfig) => {
      // Check if pattern matches
      if (!domainConfig.pattern.test(url.origin)) {
        return false;
      }

      // Protocol validation
      if (url.protocol === 'https:') {
        return true; // HTTPS is always allowed
      }

      if (url.protocol === 'http:') {
        // HTTP is only allowed if domain config explicitly allows it
        return domainAllowsHttp(domainConfig);
      }

      return false;
    });

    return matchingDomain ? url.origin : null;
  } catch (e) {
    // Invalid URL format
    return null;
  }
}

// Re-export utility functions for convenience
export { isInIframe, isCurrentLocationLocalhost, isSecureContext } from './iframeSecurity.utils';

/**
 * Get the parent window's origin
 * Returns null if not in iframe or if origin cannot be accessed
 *
 * Security: Validates and sanitizes origin to prevent injection attacks
 * Uses allowed domain configurations to determine if HTTP is permitted
 */
export function getParentOrigin(): string | null {
  if (!isInIframe()) {
    return null;
  }

  // Try to access parent location directly
  try {
    const origin = window.top?.location.origin;
    if (origin) {
      return validateAndSanitizeOrigin(origin, ALLOWED_DOMAINS);
    }
  } catch (e) {
    // Cross-origin iframe - we can't access parent origin directly
    // This is expected behavior for security (same-origin policy)
  }

  // Fallback: Try document.referrer (less reliable but sometimes available)
  try {
    if (document.referrer) {
      const sanitized = validateAndSanitizeOrigin(document.referrer, ALLOWED_DOMAINS);
      if (sanitized) {
        return sanitized;
      }
    }
  } catch (referrerError) {
    // Silently fail - this is expected in some security contexts
  }

  return null;
}

/**
 * Check if the parent origin is in the allowed list
 * Returns true if:
 * - Not in iframe (standalone mode)
 * - Parent origin matches one of the allowed domains
 * - Running on localhost (development mode)
 *
 * Security: Uses constant-time comparison where possible to prevent timing attacks
 */
export function isAllowedIframeOrigin(): boolean {
  // If not in iframe, always allow
  if (!isInIframe()) {
    return true;
  }

  // Allow localhost for development (when app itself is running on localhost)
  if (isCurrentLocationLocalhost()) {
    return true;
  }

  const parentOrigin = getParentOrigin();

  // If we can't determine parent origin, be conservative and block
  // This prevents bypass attempts where origin cannot be verified
  if (!parentOrigin) {
    if (isDevelopmentEnv()) {
      console.warn('Security: Cannot determine parent origin - blocking iframe access');
    }
    return false;
  }

  // Parent origin is already validated and sanitized in getParentOrigin()
  // If it's not null, it means it matched an allowed domain pattern
  // Return true since validation already passed
  return true;
}

/**
 * Check if we should use SameSite=None for cookies
 * Only use it if:
 * - In iframe
 * - Parent origin is allowed
 * - Context is secure (HTTPS or localhost)
 *
 * Security: This ensures cookies are only set with SameSite=None for
 * authorized iframe contexts, preventing CSRF attacks
 */
export function shouldUseSameSiteNone(): boolean {
  if (!isInIframe()) {
    return false;
  }

  // Must verify parent origin is allowed before using SameSite=None
  if (!isAllowedIframeOrigin()) {
    return false;
  }

  // Check if context is secure (HTTPS required for SameSite=None, except localhost)
  if (!isSecureContext()) {
    // SameSite=None requires Secure flag, which requires HTTPS (except for localhost)
    return false;
  }

  return true;
}

/**
 * Get allowed iframe domains for display purposes (sanitized)
 * Used for error messages and debugging
 */
export function getAllowedIframeDomains(): string[] {
  return getAllowedDomainPatterns();
}
